import React, { useEffect, useState } from 'react';
import { func, string } from 'prop-types';
import classNames from 'classnames';

import { searchCarModel } from '../../util/api';
import { versionLabel } from '../../util/carVersion';

import css from './CarModelPicker.module.css';

/* Every make with its models, fetched once per page load and shared by every
 * picker on the page (the list changes only when the car database is
 * reimported). A failed or empty answer is forgotten, so the next picker
 * tries again. */
let makesRequest = null;
const loadMakes = () => {
  if (!makesRequest) {
    makesRequest = Promise.resolve(searchCarModel({ list: 'makes' }))
      .then(response => {
        const makes = Array.isArray(response?.makes) ? response.makes : [];
        if (makes.length === 0) makesRequest = null;
        return makes;
      })
      .catch(err => {
        makesRequest = null;
        throw err;
      });
  }
  return makesRequest;
};

/* "Polestar engineered (U) 2019–"; the server's own label when the parts are
 * missing (an older API answer). */
const optionLabel = version =>
  versionLabel(version) ||
  String(version?.name || '')
    .split(' | ')
    .slice(2)
    .join(' ');

/**
 * Choose a car without a registration number: Bilmärke → Modell →
 * Modellversion → Visa (P25009-491 BR-6).
 *
 * Everything comes from the car database (CarFitment), the same rows a plate
 * lookup lands on, so the chosen car has the full fitment — bolt pattern, hub
 * bore, sizes per axle — and the search page's size chips and fit checks work
 * exactly as for a plate. The version step is what makes it one car: an XC60
 * 2008–2017 (D) and an XC60 2017– (U) take different wheels.
 *
 * Built to be shared: the search page's "Byt bil" panel uses it, and the
 * car-database-as-truth work (P25009-471) needs the same picker on the create
 * listing page.
 *
 * `onResolved(payload, { make, model, version })` receives the car as
 * `{ data: [car] }` — the shape a plate lookup returns.
 */
const CarModelPicker = props => {
  const { className, idPrefix, onResolved, submitLabel } = props;

  const [makes, setMakes] = useState([]);
  const [makesFailed, setMakesFailed] = useState(false);
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [version, setVersion] = useState('');
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    loadMakes()
      .then(list => {
        if (!cancelled) setMakes(list);
      })
      .catch(() => {
        if (!cancelled) setMakesFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /* A model's versions; a model with one version is that version. */
  useEffect(() => {
    setVersions([]);
    setVersion('');
    if (!make || !model) return undefined;
    let cancelled = false;
    setVersionsLoading(true);
    searchCarModel({ make, model })
      .then(response => {
        if (cancelled) return;
        const list = Array.isArray(response?.availableSeries) ? response.availableSeries : [];
        setVersions(list);
        if (list.length === 1) setVersion(list[0].slug);
      })
      .catch(() => {
        if (!cancelled) setError('Kunde inte hämta modellversioner. Försök igen.');
      })
      .finally(() => {
        if (!cancelled) setVersionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [make, model]);

  const models = makes.find(m => m.make === make)?.models || [];

  const submit = () => {
    if (!make || !model || !version || resolving) return;
    setResolving(true);
    setError(null);
    searchCarModel({ make, model, series: version })
      .then(payload => {
        if (!payload?.data?.[0]) {
          setError('Vi hittade inga hjulmått för den versionen.');
          return;
        }
        const chosen = versions.find(v => v.slug === version) || null;
        onResolved(payload, { make, model, version: chosen });
      })
      .catch(() => setError('Kunde inte hämta bilen. Försök igen.'))
      .finally(() => setResolving(false));
  };

  const id = name => `${idPrefix}-${name}`;

  return (
    <div className={classNames(css.root, className)}>
      <div className={css.row}>
        <label className={css.field} htmlFor={id('make')}>
          <span className={css.label}>Bilmärke</span>
          <select
            id={id('make')}
            className={css.select}
            value={make}
            onChange={e => {
              setMake(e.target.value);
              setModel('');
              setError(null);
            }}
            disabled={makes.length === 0}
          >
            <option value="">{makesFailed ? 'Kunde inte hämta märken' : 'Välj märke'}</option>
            {makes.map(m => (
              <option key={m.make} value={m.make}>
                {m.make}
              </option>
            ))}
          </select>
        </label>
        <label className={css.field} htmlFor={id('model')}>
          <span className={css.label}>Modell</span>
          <select
            id={id('model')}
            className={css.select}
            value={model}
            onChange={e => {
              setModel(e.target.value);
              setError(null);
            }}
            disabled={!make}
          >
            <option value="">Välj modell</option>
            {models.map(name => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className={css.row}>
        <label className={classNames(css.field, css.versionField)} htmlFor={id('version')}>
          <span className={css.label}>Modellversion</span>
          <select
            id={id('version')}
            className={css.select}
            value={version}
            onChange={e => setVersion(e.target.value)}
            disabled={!model || versionsLoading || versions.length === 0}
          >
            <option value="">
              {versionsLoading
                ? 'Hämtar…'
                : model && versions.length === 0
                  ? 'Inga versioner'
                  : 'Välj version'}
            </option>
            {versions.map(v => (
              <option key={v.slug} value={v.slug}>
                {optionLabel(v)}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className={css.submit}
          onClick={submit}
          disabled={!version || resolving}
        >
          {resolving ? 'Hämtar…' : submitLabel}
        </button>
      </div>
      {error ? (
        <p className={css.error} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
};

CarModelPicker.defaultProps = {
  className: null,
  idPrefix: 'carModelPicker',
  submitLabel: 'Visa',
};

CarModelPicker.propTypes = {
  className: string,
  idPrefix: string,
  onResolved: func.isRequired,
  submitLabel: string,
};

export default CarModelPicker;
